/**
 * CanvasLegend — top-right toggle + slide-in panel listing the 5 node types
 * and 3 source indicators.
 *
 * Phase 4.4 visual port of the prototype's `.canvas__legend-wrap` /
 * `.legend-panel`. Clicking a node-type row toggles its visibility on the
 * canvas — the parent (ProcessCanvas) owns `hiddenTypes` state and decides
 * which nodes to mark `hidden: true`.
 */

import { useState } from 'react';
import { LayoutGrid } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TYPE_META, type NodeType } from '@/lib/nodeTypes';

const NODE_TYPE_ORDER: NodeType[] = ['step', 'actor', 'tool', 'insight', 'trigger'];

const SOURCE_ITEMS: { key: string; label: string; render: () => React.ReactNode }[] = [
  {
    key: 'new',
    label: 'New (this interview)',
    render: () => (
      <span
        aria-hidden="true"
        className="inline-block flex-shrink-0"
        style={{
          width: 14,
          height: 10,
          background: 'hsl(var(--muted))',
          border: '1.5px solid hsl(var(--foreground))',
          borderRadius: 3,
        }}
      />
    ),
  },
  {
    key: 'existing',
    label: 'Existing knowledge',
    render: () => (
      <span
        aria-hidden="true"
        className="inline-block flex-shrink-0"
        style={{
          width: 14,
          height: 10,
          background: 'hsl(var(--muted))',
          border: '1.5px dashed hsl(var(--foreground) / 0.5)',
          borderRadius: 3,
          opacity: 0.7,
        }}
      />
    ),
  },
  {
    key: 'conflict',
    label: 'Conflict',
    render: () => (
      <span
        aria-hidden="true"
        className="inline-block flex-shrink-0 animate-[highlightPulse_1.4s_ease-in-out_infinite]"
        style={{
          width: 14,
          height: 10,
          background: 'hsl(var(--muted))',
          border: '1.5px solid hsl(var(--kaiva-warning))',
          borderRadius: 3,
        }}
      />
    ),
  },
];

interface CanvasLegendProps {
  hiddenTypes: Set<string>;
  onToggleType: (type: NodeType) => void;
}

export function CanvasLegend({ hiddenTypes, onToggleType }: CanvasLegendProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="absolute right-4 top-[70px] z-10">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label={isOpen ? 'Hide legend' : 'Show legend'}
        title="Legend"
        className="inline-grid h-[30px] w-[30px] place-items-center rounded-lg transition-colors"
        style={{
          background: 'hsl(var(--card))',
          border: '1px solid hsl(var(--border))',
          color: 'hsl(var(--muted-foreground))',
          boxShadow: 'var(--shadow-1)',
        }}
      >
        <LayoutGrid className="h-3.5 w-3.5" />
      </button>

      {/* Slide-in panel */}
      {isOpen && (
        <div
          className="absolute right-0 top-[38px] animate-[fadeIn_0.15s_ease-out]"
          style={{
            width: 220,
            padding: 12,
            background: 'hsl(var(--card))',
            border: '1px solid hsl(var(--border))',
            borderRadius: 12,
            boxShadow: 'var(--shadow-pop)',
          }}
        >
          <SectionHeading>Node types</SectionHeading>
          <div className="space-y-0">
            {NODE_TYPE_ORDER.map((t) => {
              const meta = TYPE_META[t];
              const hidden = hiddenTypes.has(t);
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => onToggleType(t)}
                  title={hidden ? 'Show' : 'Hide'}
                  className={cn(
                    'flex w-full items-center gap-2 py-[3px] text-left transition-opacity duration-[120ms]',
                    hidden ? 'opacity-40' : 'opacity-100',
                  )}
                  style={{ color: 'hsl(var(--foreground))' }}
                >
                  <NodeTypeShape type={t} color={meta.color} />
                  <span className="text-[11.5px]">{meta.label}</span>
                </button>
              );
            })}
          </div>

          <div style={{ height: 12 }} />

          <SectionHeading>Source</SectionHeading>
          <div className="space-y-0">
            {SOURCE_ITEMS.map((item) => (
              <div
                key={item.key}
                className="flex items-center gap-2 py-[3px]"
                style={{ color: 'hsl(var(--foreground))' }}
              >
                {item.render()}
                <span className="text-[11.5px]">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h4
      className="m-0 mb-2 kv-eyebrow"
      style={{
        fontSize: 10,
        fontWeight: 600,
        color: 'hsl(var(--muted-foreground))',
      }}
    >
      {children}
    </h4>
  );
}

/** Type-specific swatch shape — mirrors the prototype's `.shape-*` rules. */
function NodeTypeShape({ type, color }: { type: NodeType; color: string }) {
  const baseStyle: React.CSSProperties = {
    width: 14,
    height: 10,
    background: `hsl(${color})`,
    flexShrink: 0,
  };

  switch (type) {
    case 'step':
      return <span aria-hidden="true" className="inline-block" style={{ ...baseStyle, borderRadius: 3 }} />;
    case 'actor':
      return (
        <span
          aria-hidden="true"
          className="inline-block"
          style={{ ...baseStyle, width: 14, height: 14, borderRadius: '50%' }}
        />
      );
    case 'tool':
      return <span aria-hidden="true" className="inline-block" style={{ ...baseStyle, borderRadius: 2 }} />;
    case 'insight':
      return (
        <span
          aria-hidden="true"
          className="inline-block"
          style={{
            ...baseStyle,
            borderRadius: 3,
            border: `1px dashed hsl(${color} / 0.6)`,
          }}
        />
      );
    case 'trigger':
      return (
        <span
          aria-hidden="true"
          className="inline-block"
          style={{ ...baseStyle, borderRadius: 20 }}
        />
      );
  }
}
